import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { api } from '../../lib/api';
import type { ListResponse, TripSummary } from '../../lib/types';
import { Reveal, RevealGroup, RevealItem } from '../Reveal';
import { TripCard, TripCardSkeleton } from '../trip/TripCard';
import { ButtonLink, EmptyState, SectionHeading } from '../ui';

/**
 * Showcase.
 *
 * Reads the real gallery endpoint rather than a hard-coded list, so the trips
 * an admin marks as featured show up here with no deploy. The Turkey trip
 * arrives through the seed as the first of them.
 */
export function Showcase() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['gallery', 'featured'],
    queryFn: () =>
      api.get<ListResponse<TripSummary>>('/trips/gallery', { featured: true, limit: 3 }),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const trips = data?.items ?? [];

  return (
    <section id="showcase" className="relative overflow-hidden bg-[var(--surface-sunken)] py-24 md:py-32">
      <div className="container-page">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <SectionHeading
              eyebrow="Готовые маршруты"
              title="Не с чистого листа"
              description="Возьмите чужое путешествие за основу: маршрут, места и расписание скопируются к вам как черновик, а личные данные автора — нет."
            />
          </Reveal>
          <Reveal delay={0.1}>
            <ButtonLink to="/gallery" variant="outline">
              Вся витрина
            </ButtonLink>
          </Reveal>
        </div>

        <div className="mt-12">
          {isLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <TripCardSkeleton key={i} />
              ))}
            </div>
          ) : isError ? (
            <EmptyState
              icon="🛰️"
              title="Витрина недоступна"
              description="Не получилось связаться с сервером. Обновите страницу через минуту."
            />
          ) : trips.length === 0 ? (
            <EmptyState
              icon="🧳"
              title="Здесь скоро появятся маршруты"
              description="Первое публичное путешествие уже готовится. Загляните чуть позже — или создайте своё."
              action={<ButtonLink to="/register">Создать своё</ButtonLink>}
            />
          ) : (
            <RevealGroup className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {trips.map((trip) => (
                <RevealItem key={trip.id}>
                  <TripCard trip={trip} />
                </RevealItem>
              ))}
            </RevealGroup>
          )}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-[var(--text-muted)]">
            {[
              ['⑂', 'Копия за один клик'],
              ['📅', 'Даты сдвинутся под ваши'],
              ['🔒', 'Чужие брони не копируются'],
            ].map(([icon, label]) => (
              <motion.span
                key={label}
                whileHover={{ y: -2 }}
                className="inline-flex items-center gap-2"
              >
                <span>{icon}</span>
                {label}
              </motion.span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
